import { fetchAuthSession } from "aws-amplify/auth";
import { cognitoLoginSuccess, setCognitoConfig, setUpgradePlanFlag, setUserRole } from "src/page/auth/loginSlice";
import store from "src/page/store";
import { setShowSubscriptionModal } from "src/page/plans/plansSlice";
import { getConfig } from "src/config/Config";
import { Transaction } from "src/page/transactions/transactionSlice";
import axios from "axios";
import swal from "sweetalert2";
import { checkUserNameAvailability } from "src/network/graphql/userService";
import { CardAccessType, SpendFrequency, User } from "src/types/commonTypes";
import { EMPTY_STRING } from "src/components/constant/constant";
import { useEffect, useRef, useState } from "react";
import { formatFullName, formatMoney, getDecimalNumber } from "./formatUtils";
import { sanitizeName } from "./validationUtils";

export const refreshSession = async (refreshToken?: string) => {
  const dispatch = store.dispatch;
  const config = getConfig();
  const { auth } = store.getState();

  dispatch(setUpgradePlanFlag(false));
  const isSSOUser = !auth.changePasswordPermission;

  if (isSSOUser && refreshToken) {
    return new Promise((resolve, reject) => {
      const response = axios.post(
        `${config.cognitoConfig.baseUrl}/oauth2/token`,
        new URLSearchParams({
          grant_type: "refresh_token",
          refresh_token: refreshToken,
          client_id: config.COGNITO_CLIENT_ID
        }),
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded"
          }
        }
      );

      if (response === null || response === undefined) return reject({ message: "User session expired" });

      response
        .then((res) => {
          const accessToken = res.data.access_token;
          const idToken = res.data.id_token;

          const payload = JSON.parse(atob(idToken.split(".")[1]));

          dispatch(setUserRole(payload.current_role));
          const organisations = JSON.parse(payload.available_relations);

          const currentOrganisation = organisations.find(
            (org: { organisation_id: string }) => org.organisation_id === payload.current_organisation
          );

          dispatch(
            setCognitoConfig({
              organisations,
              currentOrganisation: currentOrganisation
            })
          );

          const structuredSession = {
            idToken: idToken,
            accessToken: accessToken,
            refreshToken: refreshToken
          };

          dispatch(cognitoLoginSuccess(structuredSession));
          return resolve(structuredSession);
        })
        .catch((err) => {
          return reject(err);
        });
    });
  }

  try {
    const session = await fetchAuthSession({ forceRefresh: true });

    if (!session.tokens?.idToken || !session.tokens?.accessToken) {
      throw new Error("Invalid session tokens");
    }

    const idToken = session.tokens.idToken.toString();
    const accessToken = session.tokens.accessToken.toString();
    const payload = session.tokens.accessToken.payload;

    if (payload.current_role) {
      dispatch(setUserRole(payload.current_role as string));
      const organisations = JSON.parse(payload.available_relations as string);

      const currentOrganisation = organisations.find(
        (org: { organisation_id: string }) => org.organisation_id === payload.current_organisation
      );

      dispatch(
        setCognitoConfig({
          organisations,
          currentOrganisation: currentOrganisation
        })
      );
    }

    const structuredSession = {
      idToken,
      accessToken,
      refreshToken
    };

    dispatch(cognitoLoginSuccess(structuredSession));
    return structuredSession;
  } catch (error) {
    console.error("Error refreshing session:", error);
  }
};

export const createUsername = (input: { firstName: string; lastName: string }) => {
  const random = Math.floor(100000000 + Math.random() * 900000000);

  return `${input.firstName}_${input.lastName}_${random}`.replace(/\s+/g, "_").toLowerCase();
};

export const generateUsername = async (firstName: string, lastName: string): Promise<string> => {
  let attempts = 0;
  let generatedUsername = EMPTY_STRING;

  do {
    const randomNumber = Math.floor(Math.random() * 900) + 100;

    generatedUsername = `${firstName}.${lastName}${randomNumber}`.toLowerCase();

    const isUserNameAvailable = await checkUserNameAvailability(generatedUsername);

    if (isUserNameAvailable.data.is_username_available) {
      return generatedUsername;
    }

    attempts++;
  } while (attempts < 3);

  return EMPTY_STRING;
};

export const mapApiUserToUser = (apiUser: any, extras?: Partial<User>): User => {
  const rawFirst = apiUser?.firstName ?? apiUser?.first_name ?? apiUser?.attributes?.first_name ?? apiUser?.name;
  const rawLast = apiUser?.lastName ?? apiUser?.last_name ?? apiUser?.attributes?.last_name ?? apiUser?.family_name;

  const firstName = sanitizeName(rawFirst);
  const lastName = sanitizeName(rawLast);
  const fullName = formatFullName(firstName, lastName);

  if (!firstName || !lastName) {
    console.warn("mapApiUserToUser: received missing/invalid name fields", {
      userId: apiUser?.user_id ?? apiUser?.id ?? apiUser?.sub,
      rawFirst,
      rawLast
    });
  }

  return {
    id: apiUser?.user_id ?? apiUser?.id ?? apiUser?.sub ?? EMPTY_STRING,
    displayId: apiUser?.display_id ?? apiUser?.displayId ?? EMPTY_STRING,
    cognitoUserName:
      apiUser?.cognito_username ?? apiUser?.cognitoUserName ?? apiUser?.["cognito:username"] ?? EMPTY_STRING,
    username: apiUser?.username,
    firstName,
    lastName,
    fullName,
    email: apiUser?.email,
    contact: apiUser?.phone ?? apiUser?.phone_number,
    createdAt: apiUser?.created_at ?? apiUser?.createdAt,
    ...extras
  };
};

export const removeNull = (obj: object) => {
  const newObj: { [_key: string]: any } = {};

  Object.entries(obj).forEach(([key, value]) => {
    if (value != null || value != undefined) {
      newObj[key] = value;
    }
  });
  return newObj;
};

export const getTransactionValue = (transaction: Transaction) => {
  const amount = "$" + formatMoney(transaction?.data?.amount);
  let value;
  let amountColor;

  if (transaction?.name === "NOVATTI_CARD_TRANSACTION") {
    if (transaction?.data?.transType === "CASH") {
      value = transaction?.data?.description;
    } else {
      value = transaction?.data?.merchantName;
    }
    amountColor = "#ff0000";
  } else if (transaction?.name === "WALLET_TRANSACTION") {
    if (transaction?.data?.type === "DEPOSIT") {
      value = "Deposit Trust U+";
      if (transaction?.data?.amount > 0) {
        amountColor = "#008000";
      } else {
        amountColor = "#ff0000";
      }
    } else if (transaction?.data?.type === "TRANSFER") {
      if (transaction?.data?.side === "OUT") {
        value = transaction?.data?.destination_wallet?.name;
        amountColor = "#ff0000";
      } else if (transaction?.data?.side === "IN") {
        value = transaction?.data?.source_wallet?.name;
        amountColor = "#008000";
      }
    } else if (transaction?.data?.type === "ADJUSTMENT") {
      value = "Adjustment Trust U+";
      if (transaction?.data?.amount > 0) {
        amountColor = "#008000";
      } else {
        amountColor = "#ff0000";
      }
    }
  } else if (transaction?.name === "CASH_TRANSACTION") {
    value = transaction?.data?.merchantName;
    amountColor = "#ff0000";
  }
  return {
    value,
    amount,
    amountColor
  };
};

export const calculateBudget = (
  balance: number = 0,
  spend_frequency: SpendFrequency,
  amountSpend?: number,
  budgetLimit?: number
): number => {
  if (!budgetLimit || spend_frequency === "NONE") return balance;
  if (amountSpend === undefined || amountSpend === null) return balance;

  const remaining = budgetLimit - amountSpend;

  return getDecimalNumber(Math.max(0, Math.min(remaining, balance)));
};

export const resolveCardAccessType = (accessType?: string): CardAccessType => {
  if (accessType === "SHARED" || accessType === "PRIVATE") {
    return accessType;
  }

  return "SHARED";
};

interface UpgradePlanProp {
  title: string;
  subTitle: string;
}

export const upgradePlan = ({ title, subTitle }: UpgradePlanProp) => {
  const dispatch = store.dispatch;

  swal
    .fire({
      icon: "warning",
      title: title,
      text: subTitle,
      showCancelButton: true,
      confirmButtonText: "Upgrade Plan",
      cancelButtonText: `I'll do it later.`,
      confirmButtonColor: "#0f172a"
    })
    .then((result) => {
      if (result.isConfirmed) {
        dispatch(setShowSubscriptionModal(true));
      }
    });
};

/**
 * Custom hook for typing animation effect
 * @param text - The text to animate
 * @param options - Configuration options for the animation
 * @returns The animated text string
 */
export const useTypingAnimation = (
  text: string,
  options?: {
    typingSpeed?: number;
    deletingSpeed?: number;
    pauseDelay?: number;
    startDelay?: number;
    enabled?: boolean;
    loop?: boolean;
  }
): string => {
  const {
    typingSpeed = 100,
    deletingSpeed = 50,
    pauseDelay = 2000,
    startDelay = 500,
    enabled = true,
    loop = true
  } = options || {};

  const [animatedText, setAnimatedText] = useState(text || EMPTY_STRING);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const typingStateRef = useRef({ currentIndex: 0, isDeleting: false });

  useEffect(() => {
    // Reset state when text or enabled changes
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingStateRef.current = { currentIndex: 0, isDeleting: false };

    if (!enabled || !text) {
      setAnimatedText(text || EMPTY_STRING);
      return;
    }

    const typeText = () => {
      const typingState = typingStateRef.current;

      if (typingState.isDeleting) {
        if (typingState.currentIndex > 0) {
          setAnimatedText(text.substring(0, typingState.currentIndex - 1));
          typingState.currentIndex--;
          typingTimeoutRef.current = setTimeout(typeText, deletingSpeed);
        } else {
          if (loop) {
            typingState.isDeleting = false;
            typingTimeoutRef.current = setTimeout(typeText, typingSpeed);
          } else {
            setAnimatedText(EMPTY_STRING);
          }
        }
      } else {
        if (typingState.currentIndex < text.length) {
          setAnimatedText(text.substring(0, typingState.currentIndex + 1));
          typingState.currentIndex++;
          typingTimeoutRef.current = setTimeout(typeText, typingSpeed);
        } else {
          if (loop) {
            typingTimeoutRef.current = setTimeout(() => {
              typingState.isDeleting = true;
              typeText();
            }, pauseDelay);
          } else {
            setAnimatedText(text);
          }
        }
      }
    };

    typingTimeoutRef.current = setTimeout(typeText, startDelay);

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      typingStateRef.current = { currentIndex: 0, isDeleting: false };
    };
  }, [text, typingSpeed, deletingSpeed, pauseDelay, startDelay, enabled, loop]);

  return animatedText || text || EMPTY_STRING;
};
